import { z } from 'zod';
import { WIDGET_CATALOG, WIDGET_TYPES, type AnyWidgetEntry, type WidgetRead } from './catalog';
import {
  matchEnum,
  parseJsonContainer,
  recoverSwallowedTail,
  toBoolean,
  toNumber,
  wrapInArray,
} from './coerce';
import { WidgetSpec, type WidgetType } from './widgets';

/**
 * Keys a model nests a widget spec under when it treats `render_widget` like a function call.
 * `arguments`/`args`/`input`/`parameters` are the shared tool-dispatch set; `widget` and `spec`
 * are the widget-only additions seen live. NOT `props`: that is the canonical shape's own key
 * (`{ type, props }`), read by `toShape` — a whole widget sent INSIDE `props`, its `type` there
 * too, is still read (`unwrapped_envelope`), since the old dialect did exactly that.
 */
export const WIDGET_ENVELOPE_KEYS = [
  'arguments',
  'args',
  'input',
  'parameters',
  'widget',
  'spec',
] as const;

/**
 * What `resolveWidget` had to do to the payload. Ordered as applied; `[]` means it was already
 * valid. Every `<path>` is relative to the WIDGET (`clamped:props.options.0.label`); the one
 * exception is `recovered_keys:<k1,k2>`, which lists sibling KEY NAMES inside `props` (the field
 * they came out of is the preceding `parsed_json_string:<path>`).
 */
export type WidgetRepair =
  | 'parsed_json_string'
  | 'unwrapped_envelope'
  | 'lifted_props'
  | `parsed_json_string:${string}`
  | `recovered_keys:${string}`
  | `dropped_tail:${string}`
  | `wrapped:${string}`
  | `coerced:${string}`
  | `dropped_null:${string}`
  | `reshaped:${string}`
  | `clamped:${string}`
  | `truncated:${string}`;

export type ResolvedWidget =
  | { ok: true; widget: WidgetSpec; repairs: WidgetRepair[] }
  | {
      ok: false;
      issues: z.core.$ZodIssue[];
      attemptedType?: string;
      /**
       * With no known `type`: the types whose parse WOULD succeed. Hint material only — the widget
       * is never rendered from them (one means "add `type: X`", two or more means ambiguous).
       */
      candidates?: WidgetType[];
    };

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const KNOWN_TYPES = new Set<string>(WIDGET_TYPES);

function customIssue(message: string, input: unknown): z.core.$ZodIssue {
  return { code: 'custom', path: [], message, input };
}

/**
 * A plain object as-is, a JSON string parsed to an object, else `undefined`.
 *
 * THE FAILURE THIS PREVENTS: the envelope lift and the `attemptedType` probe must accept the SAME
 * shapes. Written out at each site, a shape added to one is silently ignored by the other — the
 * hint would then name a type the lift refuses to unwrap.
 */
function objectOrJsonObject(value: unknown): Record<string, unknown> | undefined {
  if (isPlainObject(value)) return value;
  return typeof value === 'string' ? parseJsonContainer(value, 'object') : undefined;
}

/** `options.*.value` matches `['options', 3, 'value']`: `*` stands for any array index. */
function matchesPattern(path: PropertyKey[], pattern: string): boolean {
  const segments = pattern.split('.');
  if (segments.length !== path.length) return false;
  return segments.every((segment, i) =>
    segment === '*' ? typeof path[i] === 'number' : segment === String(path[i]),
  );
}

/**
 * Whether an over-long string may be trimmed with `…`.
 *
 * THE FAILURE THIS PREVENTS: a clamp that changes MEANING. `.length(3)` is an exact-length code
 * (`'USDT'` became the currency `'US…'`), and a `verbatim` field is data, not display text — a
 * choices option `value` is sent as the user's next message, so a trimmed one is a different
 * instruction. `exact: true` is set by BOTH zod majors on a `.length()` issue (zod 4 omits it on
 * `.max()`, zod 3 sets `false`), so the signal holds under apps/web's zod 3 too.
 */
function mayClamp(issue: z.core.$ZodIssue, verbatim: readonly string[]): boolean {
  if ('exact' in issue && issue.exact === true) return false;
  return !verbatim.some((pattern) => matchesPattern(issue.path, pattern));
}

function getAt(root: unknown, path: PropertyKey[]): unknown {
  let cursor: unknown = root;
  for (const key of path) {
    if (Array.isArray(cursor) && typeof key === 'number') cursor = cursor[key];
    else if (isPlainObject(cursor)) cursor = cursor[String(key)];
    else return undefined;
  }
  return cursor;
}

/** Immutable set: clones every container along `path` so a failed round never mutates the caller's input. */
function setAt(root: unknown, path: PropertyKey[], value: unknown): unknown {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(root) && typeof head === 'number') {
    const copy = [...root];
    copy[head] = setAt(root[head], rest, value);
    return copy;
  }
  if (isPlainObject(root)) {
    const key = String(head);
    return { ...root, [key]: setAt(root[key], rest, value) };
  }
  return root;
}

type Fix = { next: unknown; repairs: WidgetRepair[] } | undefined;

/** What every per-code fixer reads: the payload so far, the issue, and the value at its path. */
interface IssueSite {
  candidate: unknown;
  issue: z.core.$ZodIssue;
  path: PropertyKey[];
  label: string;
  actual: unknown;
  entry: AnyWidgetEntry;
}

const set = (site: IssueSite, value: unknown, repair: WidgetRepair): Fix => ({
  next: setAt(site.candidate, site.path, value),
  repairs: [repair],
});

const samePath = (a: PropertyKey[], b: PropertyKey[]): boolean =>
  a.length === b.length && a.every((k, i) => String(k) === String(b[i]));

/**
 * Delete a `null` the schema does not need, or `undefined` when the field is required.
 *
 * THE FAILURE THIS PREVENTS: `stat_group.stats[].spark: null` refused the whole group — `null` on
 * an optional field says "no value", which is what omitting it says. Optional is decided by the
 * SCHEMA, not by a list: the key is removed on a copy and the entry re-parsed; if the path is
 * still reported (a required field, now missing) nothing is dropped. The zod parse is the one
 * definition of "required" both zod majors share — `z.toJSONSchema` does not exist in the zod 3
 * apps/web bundles this source against, so reading a JSON-schema `required` would diverge there.
 */
function dropNull(site: IssueSite): Fix {
  const parentPath = site.path.slice(0, -1);
  const parent = getAt(site.candidate, parentPath);
  if (!isPlainObject(parent)) return undefined;
  const without = { ...parent };
  delete without[String(site.path.at(-1))];
  const next = setAt(site.candidate, parentPath, without);
  const probe = site.entry.spec.safeParse(next);
  if (!probe.success && probe.error.issues.some((i) => samePath(i.path, site.path))) {
    return undefined;
  }
  return { next, repairs: [`dropped_null:${site.label}`] };
}

/**
 * A top-level field whose string swallowed the rest of the widget (see `parseSwallowedTail`):
 * the field becomes its container and the swallowed siblings the model did not send explicitly
 * are added; an unreadable tail is dropped (`dropped_tail:<field>`) — a widget without, say, its
 * optional `prompt` still renders what the model meant. Top-level only — the prefix it re-attaches is the WIDGET's, not a nested object's.
 */
function fixSwallowedTail(site: IssueSite, text: string, want: 'array' | 'object'): Fix {
  if (site.path.length !== 1 || !isPlainObject(site.candidate)) return undefined;
  const tail = recoverSwallowedTail(site.candidate, String(site.path[0]), text, want);
  if (!tail) return undefined;
  const repairs: WidgetRepair[] = [`parsed_json_string:${site.label}`];
  if (tail.recovered.length) repairs.push(`recovered_keys:${tail.recovered.join(',')}`);
  if (tail.droppedTail) repairs.push(`dropped_tail:${site.label}`);
  return { next: tail.next, repairs };
}

/** A container sent as a JSON string, or a lone item where a list belongs. */
function fixContainer(site: IssueSite, want: 'array' | 'object'): Fix {
  const { actual, label } = site;
  if (typeof actual === 'string') {
    const parsed =
      want === 'array' ? parseJsonContainer(actual, 'array') : parseJsonContainer(actual, 'object');
    if (parsed !== undefined) return set(site, parsed, `parsed_json_string:${label}`);
    return fixSwallowedTail(site, actual, want);
  }
  if (want !== 'array') return undefined;
  const wrapped = wrapInArray(actual);
  return wrapped === undefined ? undefined : set(site, wrapped, `wrapped:${label}`);
}

/** zod 4 spells a record `record`, zod 3 `object`: both mean "a JSON object belongs here". */
const CONTAINER_OF: Record<string, 'array' | 'object'> = {
  array: 'array',
  object: 'object',
  record: 'object',
};

function fixInvalidType(site: IssueSite): Fix {
  const { actual, label } = site;
  // `expected` is spelled the same in both zod majors; read structurally so zod 3's types agree.
  const expected = String((site.issue as { expected?: unknown }).expected);
  if (actual === null) return dropNull(site);
  if (expected === 'number' && typeof actual === 'string') {
    const n = toNumber(actual);
    return n === undefined ? undefined : set(site, n, `coerced:${label}`);
  }
  if (expected === 'boolean' && typeof actual === 'string') {
    const b = toBoolean(actual);
    return b === undefined ? undefined : set(site, b, `coerced:${label}`);
  }
  if (expected === 'string' && (typeof actual === 'number' || typeof actual === 'boolean')) {
    return set(site, String(actual), `coerced:${label}`);
  }
  const container = CONTAINER_OF[expected];
  return container ? fixContainer(site, container) : undefined;
}

/** An enum miss's allowed values: zod 4 `invalid_value.values`, zod 3 `invalid_enum_value.options`. */
function enumOptions(issue: z.core.$ZodIssue): readonly unknown[] | undefined {
  const loose = issue as { code: string; values?: unknown; options?: unknown };
  if (loose.code === 'invalid_value' && Array.isArray(loose.values)) return loose.values;
  if (loose.code === 'invalid_enum_value' && Array.isArray(loose.options)) return loose.options;
  return undefined;
}

function fixEnum(site: IssueSite, options: readonly unknown[]): Fix {
  if (typeof site.actual !== 'string') return undefined;
  const matched = matchEnum(site.actual, options);
  return matched === undefined ? undefined : set(site, matched, `coerced:${site.label}`);
}

/** A cosmetic overflow is trimmed, never fatal: the cap exists for layout, not for meaning. */
function fixTooBig(site: IssueSite): Fix {
  const { actual, label, issue } = site;
  const maximum = Number((issue as { maximum?: unknown }).maximum);
  if (!Number.isFinite(maximum)) return undefined;
  if (
    typeof actual === 'string' &&
    actual.length > maximum &&
    maximum >= 1 &&
    mayClamp(issue, site.entry.verbatim ?? [])
  ) {
    return set(site, `${actual.slice(0, maximum - 1)}…`, `clamped:${label}`);
  }
  if (Array.isArray(actual) && actual.length > maximum) {
    return set(site, actual.slice(0, maximum), `truncated:${label}`);
  }
  return undefined;
}

/**
 * ONE fix for ONE issue, or `undefined` when the issue is not deterministically repairable.
 *
 * The decision is made from the VALUE at the path, not from the issue's own metadata: issue field
 * names differ between zod majors, and apps/web bundles this source against its own zod. The
 * exceptions are `exact` (see `mayClamp`) and `expected`, which both majors spell the same, and an
 * enum's allowed values, read under both majors' names (`enumOptions`).
 */
function fixIssue(candidate: unknown, issue: z.core.$ZodIssue, entry: AnyWidgetEntry): Fix {
  const path = issue.path;
  if (path.length === 0) return undefined; // a root-level union/discriminator miss is not a field slip
  const site: IssueSite = {
    candidate,
    issue,
    path,
    // Widget-relative: the issue came from the PROPS schema; the label names the widget path.
    label: ['props', ...path].map(String).join('.'),
    actual: getAt(candidate, path),
    entry,
  };
  if (issue.code === 'invalid_type') return fixInvalidType(site);
  if (issue.code === 'too_big') return fixTooBig(site);
  const options = enumOptions(issue);
  return options ? fixEnum(site, options) : undefined;
}

type EntryAttempt =
  | { ok: true; props: unknown; repairs: WidgetRepair[] }
  | { ok: false; issues: z.core.$ZodIssue[] };

const MAX_COERCION_ROUNDS = 3;

/**
 * One catalog entry's own normalize + the issue-driven coercion loop, over the PROPS object (the
 * entry's `spec` is its props schema). Issues come back WIDGET-relative (`props.<path>`), the
 * same paths the repair labels use. `normalize` runs again after
 * every round that changed something: a `columns`/`stats` array that arrived as a JSON string only
 * becomes an array inside the loop, and its `{ id, header }` aliases still need the entry's renames.
 */
function tryEntry(
  entry: AnyWidgetEntry,
  input: Record<string, unknown>,
  read: WidgetRead,
): EntryAttempt {
  const repairs: WidgetRepair[] = [];
  const normalize = (raw: unknown): unknown => {
    if (!entry.normalize || !isPlainObject(raw)) return raw;
    const normalized = entry.normalize(raw, read);
    for (const r of normalized.repairs) if (!repairs.includes(r)) repairs.push(r);
    return normalized.value;
  };
  let candidate = normalize(input);
  let parsed = entry.spec.safeParse(candidate);

  for (let round = 0; round < MAX_COERCION_ROUNDS && !parsed.success; round++) {
    let changed = false;
    for (const issue of parsed.error.issues) {
      const fix = fixIssue(candidate, issue, entry);
      if (!fix) continue;
      candidate = fix.next;
      repairs.push(...fix.repairs);
      changed = true;
    }
    // Never loop on an issue the fix did not change.
    if (!changed) break;
    candidate = normalize(candidate);
    parsed = entry.spec.safeParse(candidate);
  }

  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((issue) => ({ ...issue, path: ['props', ...issue.path] })),
    };
  }
  const broken = read === 'live' ? (entry.liveRules?.(parsed.data) ?? []) : [];
  if (broken.length === 0) return { ok: true, props: parsed.data, repairs };
  return {
    ok: false,
    issues: broken.map(({ path, message }) => ({
      ...customIssue(message, getAt(parsed.data, path)),
      path: ['props', ...path],
    })),
  };
}

/** The payload split into the canonical halves, and what it took to get there. */
interface Shaped {
  type: unknown;
  props: Record<string, unknown>;
  repairs: WidgetRepair[];
}

/**
 * Split any payload into `{ type, props }`: canonical as-is, the legacy FLAT dialect lifted.
 *
 * THE FAILURE THIS PREVENTS: the flat shape (`{ type, chartType, data }`) is what every persisted
 * widget and every older few-shot carries, and a model that learned it still writes it. Refused,
 * it would blank ~16,000 stored widgets and bounce every old-dialect call; accepted silently, the
 * `lifted_props` count — the signal that the model still speaks the old dialect — would vanish.
 * The crossovers are explicit: a stray `type` inside `props` equal to the widget's is dropped;
 * with no outer `type`, one inside `props` IS the declared type (the whole widget sent inside
 * `props`); flat keys beside a `props` object are merged in, and the `props` object wins.
 */
function toShape(candidate: Record<string, unknown>): Shaped {
  const { type, props: rawProps, ...flat } = candidate;
  const nested = objectOrJsonObject(rawProps);
  if (!nested) return { type, props: flat, repairs: ['lifted_props'] };

  const repairs: WidgetRepair[] = typeof rawProps === 'string' ? ['parsed_json_string:props'] : [];
  const props = { ...nested };
  let declared = type;
  if (declared === undefined && typeof props.type === 'string') {
    declared = props.type;
    delete props.type;
    repairs.push('unwrapped_envelope');
  } else if (props.type === declared) {
    delete props.type;
  }
  const flatKeys = Object.keys(flat);
  if (flatKeys.length) {
    for (const key of flatKeys) if (!(key in props)) props[key] = flat[key];
    repairs.push('lifted_props');
  }
  return { type: declared, props, repairs };
}

type ParseAsResult =
  | { ok: true; widget: WidgetSpec; repairs: WidgetRepair[] }
  | { ok: false; issues: z.core.$ZodIssue[]; candidates?: WidgetType[] };

/**
 * Parse one already-unwrapped object under the `type` it declares.
 *
 * A missing or unknown `type` is REFUSED, never inferred. THE FAILURE THIS PREVENTS: the type is
 * MISSING information, not a mis-formed value — zod strips unknown keys, so `{ label, value, low,
 * high }` parses as a stat_card AND a range_indicator, and a bare `{ items }` as whichever entry
 * owns an `items` array. The types that WOULD accept the payload are still computed and returned
 * as `candidates`, so the hint can say "this looks like a table — add `type`".
 */
function parseAs(candidate: Record<string, unknown>, read: WidgetRead): ParseAsResult {
  const shaped = toShape(candidate);
  const declared = shaped.type;
  if (typeof declared === 'string' && KNOWN_TYPES.has(declared)) {
    const type = declared as WidgetType;
    const attempt = tryEntry(WIDGET_CATALOG[type], shaped.props, read);
    if (!attempt.ok) return { ok: false, issues: attempt.issues };
    // The props parsed against THIS type's own schema, so the pair is that type's variant.
    const widget = { type, props: attempt.props } as WidgetSpec;
    return { ok: true, widget, repairs: [...shaped.repairs, ...attempt.repairs] };
  }
  const candidates = WIDGET_TYPES.filter(
    (type) => tryEntry(WIDGET_CATALOG[type], shaped.props, read).ok,
  );
  // The honest report of what the model sent: the union's own discriminator complaint.
  const asUnion = WidgetSpec.safeParse(candidate);
  const issues = asUnion.success ? [] : asUnion.error.issues;
  return candidates.length ? { ok: false, issues, candidates } : { ok: false, issues };
}

/** The `type` the model was reaching for — inside an envelope too, so a hint can name its shape. */
function attemptedType(raw: Record<string, unknown>): string | undefined {
  if (typeof raw.type === 'string') return raw.type;
  for (const key of [...WIDGET_ENVELOPE_KEYS, 'props']) {
    const inner = objectOrJsonObject(raw[key]);
    if (inner && typeof inner.type === 'string') return inner.type;
  }
  return undefined;
}

/**
 * Lift one envelope: outer keys minus the envelope key (and a `render_widget`-style `name`), with
 * the inner object merged OVER them. Inner wins, so `type` may sit outside OR inside the envelope.
 */
function lift(raw: Record<string, unknown>, key: string, inner: Record<string, unknown>) {
  const outer = { ...raw };
  delete outer[key];
  if (typeof outer.name === 'string') delete outer.name;
  return { ...outer, ...inner };
}

/**
 * The whole spec JSON-encoded INSIDE `type` (`{ type: "{\"type\":\"chart\",…}" }`), lifted out.
 * THE FAILURE THIS PREVENTS: 3 prod charts refused as "not a widget type" while carrying a
 * complete, valid spec one level of quoting down.
 */
function specInsideType(raw: Record<string, unknown>): Record<string, unknown> | undefined {
  if (typeof raw.type !== 'string') return undefined;
  const inner = parseJsonContainer(raw.type, 'object');
  return inner && typeof inner.type === 'string' ? lift(raw, 'type', inner) : undefined;
}

/** A payload sent as a JSON string, parsed; `undefined` input means it was a string but not JSON. */
function parsePayload(raw: unknown): { input: unknown; repairs: WidgetRepair[] } | undefined {
  if (typeof raw !== 'string') return { input: raw, repairs: [] };
  const parsed = parseJsonContainer(raw, 'object');
  return parsed ? { input: parsed, repairs: ['parsed_json_string'] } : undefined;
}

/**
 * The ONE healer: turn whatever the model sent into a valid canonical `{ type, props }`
 * `WidgetSpec`, or say precisely why not. Legacy flat input is converted (`lifted_props`).
 *
 * THE FAILURE THIS PREVENTS: a quarter of prod `render_widget` calls arrive with the RIGHT
 * information in the WRONG FORM — a JSON string (whole, inside `type`, or one field deep), a
 * `{ name, arguments }` envelope, a cosmetic cap overflow, an enum alias, a `null` on an optional
 * field, a number sent as a string, wide multi-series rows. Each is repaired here; bounced back,
 * the model recovered on retry at the cost of a turn the user watched. MISSING information — a
 * required field, or the `type` itself — is refused with a hint, and a value that could mean two
 * things is refused, never guessed. Nothing here does I/O or invents a figure.
 *
 * `read` defaults to `live` — a model writing now, which is refused what it left out. Only
 * `toWidgetSpec` reads `stored`, where a persisted widget that predates a rule is healed instead.
 */
export function resolveWidget(raw: unknown, read: WidgetRead = 'live'): ResolvedWidget {
  const payload = parsePayload(raw);
  if (!payload) {
    return {
      ok: false,
      issues: [customIssue('Widget input is a string that is not a JSON object.', raw)],
    };
  }
  const prefix = [...payload.repairs];
  if (!isPlainObject(payload.input)) {
    return {
      ok: false,
      issues: [customIssue('Widget input must be an object with a `type`.', raw)],
    };
  }
  const unquoted = specInsideType(payload.input);
  if (unquoted) prefix.push('parsed_json_string');
  const input = unquoted ?? payload.input;

  const asSent = parseAs(input, read);
  if (asSent.ok)
    return { ok: true, widget: asSent.widget, repairs: [...prefix, ...asSent.repairs] };

  // One level of envelope, first key that yields a valid widget.
  let candidates = asSent.candidates ?? [];
  for (const key of WIDGET_ENVELOPE_KEYS) {
    const inner = objectOrJsonObject(input[key]);
    if (!inner) continue;
    const lifted = parseAs(lift(input, key, inner), read);
    if (lifted.ok) {
      return {
        ok: true,
        widget: lifted.widget,
        repairs: [...prefix, 'unwrapped_envelope', ...lifted.repairs],
      };
    }
    if (!candidates.length) candidates = lifted.candidates ?? [];
  }

  const type = attemptedType(input);
  return {
    ok: false,
    issues: asSent.issues,
    ...(type ? { attemptedType: type } : {}),
    ...(candidates.length ? { candidates } : {}),
  };
}

/**
 * THE read path for a PERSISTED widget: `resolveWidget` with the repairs discarded — the canonical
 * `{ type, props }`, or `undefined` when even the healer refuses it.
 *
 * THE FAILURE THIS PREVENTS: ~16,000 stored widgets (`tool-render_widget` outputs, `data-widget`
 * parts, board versions) carry the legacy flat shape and are never migrated. A renderer or replay
 * that parses them itself either blanks them (canonical-only) or grows a second, drifting copy of
 * the conversion. One entry point, and it IS the healer: an old widget that fails today's schema
 * over a cosmetic overflow (a label past its cap) is repaired on read, exactly as a live call is.
 * It reads `stored`: a rule added after the widget was written (a percentage column's unit) is
 * healed from what the widget holds, never refused.
 */
export function toWidgetSpec(raw: unknown): WidgetSpec | undefined {
  const resolved = resolveWidget(raw, 'stored');
  return resolved.ok ? resolved.widget : undefined;
}

/** A known widget `type` with its fields beside it and no `props` object — the legacy flat shape. */
export function isLegacyWidget(raw: unknown): boolean {
  return (
    isPlainObject(raw) &&
    typeof raw.type === 'string' &&
    KNOWN_TYPES.has(raw.type) &&
    !isPlainObject(raw.props)
  );
}
